import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import useApiClient, { translationApi } from '../services/api';
import documentService from '../services/documentService';
import Spinner from '../components/Spinner';
import DocumentViewer from '../components/DocumentViewer';
import ConfirmDialog from '../components/ConfirmDialog';
import ApprovalRequestModal from '../components/ApprovalRequestModal';
import GoogleDocsService from '../services/googleDocsService';
import { useAuth } from '../context/AuthContext';
import SignatureStampView from '../components/SignatureStampView';
import FinalDocumentViewer from '../components/FinalDocumentViewer';
import useUserService from '../hooks/useUserService';

const ReadyForApprovalIcon = ({ size = 24 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect x="3" y="2.5" width="12" height="17" rx="1.8" fill="#F2D8B3" stroke="#C8A97E" />
    <path d="M5.8 7.2H12.2" stroke="#B29467" strokeWidth="1.2" strokeLinecap="round" />
    <path d="M5.8 10H10.8" stroke="#B29467" strokeWidth="1.2" strokeLinecap="round" />
    <path d="M5.8 12.8H10.8" stroke="#B29467" strokeWidth="1.2" strokeLinecap="round" />
    <path d="M5.8 15.6H12.2" stroke="#B29467" strokeWidth="1.2" strokeLinecap="round" />
    <circle cx="15.5" cy="15.5" r="5.2" fill="#BFDFF5" stroke="#1F4B6B" strokeWidth="1.6" />
    <path d="M13.3 15.5L14.8 17L17.8 14.2" stroke="#56B05B" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M19.2 19.3L21.5 21.6" stroke="#EF4444" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);

const ProjectDetails = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const apiClient = useApiClient();
  const { googleAccessToken, user } = useAuth();
  
  const [project, setProject] = useState(null);
  const [translations, setTranslations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    clientName: '',
    translatorName: '',
    country: '',
    dueDate: '',
    sourceLanguage: '',
    targetLanguage: ''
  });
  const [retryingTranslations, setRetryingTranslations] = useState(new Set());
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    translationId: null,
    translationName: ''
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [approvalDialog, setApprovalDialog] = useState({
    isOpen: false,
    projectId: null,
    projectName: ''
  });
  const [approvalForm, setApprovalForm] = useState({
    approverName: '',
    approverEmail: '',
    approverLanguage: 'fr'
  });
  const [isSendingApproval, setIsSendingApproval] = useState(false);
  const [approvalRequests, setApprovalRequests] = useState([]);
  const [showApprovalDetails, setShowApprovalDetails] = useState(false);
  const [selectedApprovalComment, setSelectedApprovalComment] = useState(null);
  const [sharingProgress, setSharingProgress] = useState({ current: 0, total: 0 });
  const [sharingErrors, setSharingErrors] = useState([]);
  const [documentComments, setDocumentComments] = useState({});
  const [loadingComments, setLoadingComments] = useState({});
  const [selectedDocumentComments, setSelectedDocumentComments] = useState(null);
  const [showSignatureView, setShowSignatureView] = useState(false);
  const [selectedSigningTranslation, setSelectedSigningTranslation] = useState(null);
  const [finalDocument, setFinalDocument] = useState(null);
  const [recentFinalDocument, setRecentFinalDocument] = useState(null);
  const [loadingFinalPreview, setLoadingFinalPreview] = useState(false);
  const [sendToClientForm, setSendToClientForm] = useState({ approverName: '', approverEmail: '', approverLanguage: 'fr', includeDocuments: false, includeCombinedFinal: false });
  const [sendToClientTargetVersion, setSendToClientTargetVersion] = useState(null);
  const [selectedSignedDocumentIds, setSelectedSignedDocumentIds] = useState([]);
  const [sendToClientFiles, setSendToClientFiles] = useState([]);
  const [sendToClientUploadError, setSendToClientUploadError] = useState('');
  const [isSendingToClient, setIsSendingToClient] = useState(false);
  const [showSendToClientModal, setShowSendToClientModal] = useState(false);
  const [signWarningDialog, setSignWarningDialog] = useState({
    isOpen: false,
    pendingApprovals: 0,
    openComments: 0
  });

  // Get user profile for signature and stamp
  const { userProfile, loading: profileLoading } = useUserService();
  const signedDocumentOptions = (translations || [])
    .map((translation) => {
      const latest = translation.latestFinalDocument || translation.finalDocuments?.[0] || null;
      if (!latest?.id) return null;
      return {
        id: latest.id,
        translationId: translation.id,
        label: translation.originalFileName || t('common.unknownDocument'),
        version: latest.version,
        versionLabel: `Signed v${latest.version}`,
        sentAt: latest.sentAt,
      };
    })
    .filter(Boolean);
  const hasSignedDocumentFinals = signedDocumentOptions.length > 0;

  const getFinalDocumentDownloadUrl = (finalDocument) => {
    if (!finalDocument) return '';
    if (finalDocument.downloadUrl) return finalDocument.downloadUrl;
    if (finalDocument.dropboxUrl) {
      return finalDocument.dropboxUrl
        .replace('dl=0', 'dl=1')
        .replace('www.dropbox.com', 'dl.dropboxusercontent.com');
    }
    return '';
  };

  // Prevent redundant API calls when apiClient recreates
  const hasFetched = useRef(false);

  // Reset fetch guard when projectId changes
  useEffect(() => {
    hasFetched.current = false;
  }, [projectId]);

  useEffect(() => {
    return () => {
      if (finalDocument?.blobUrl) {
        URL.revokeObjectURL(finalDocument.blobUrl);
      }
    };
  }, [finalDocument?.blobUrl]);

  useEffect(() => {
    // Guard: Skip if apiClient not ready or already fetched
    if (!apiClient || hasFetched.current) return;
    hasFetched.current = true;

    const fetchProjectDetails = async () => {
      try {
        setLoading(true);

        // Batch all API calls using Promise.all() for better performance
        const [projectResponse, translationsResponse, approvalsResponse] = await Promise.all([
          apiClient.get(`/projects/${projectId}`),
          apiClient.get(`/translations?projectId=${projectId}`),
          apiClient.get(`/approvals/project/${projectId}`)
            .catch((error) => {
              console.error('Failed to fetch approvals:', error);
              toast.error(t('projectDetails.messages.loadApprovalDataFailed'));
              return { data: { success: false } };
            })
        ]);

        // Process project data
        const projectData = projectResponse.data.project || projectResponse.data;
        setProject(projectData);
        setEditForm({
          name: projectData.name || '',
          description: projectData.description || '',
          clientName: projectData.clientName || '',
          translatorName: projectData.translatorName || '',
          country: projectData.country || '',
          dueDate: projectData.dueDate ? projectData.dueDate.split('T')[0] : '',
          sourceLanguage: projectData.sourceLanguage || '',
          targetLanguage: projectData.targetLanguage || ''
        });

        // Process translations data
        console.log('Translations response:', translationsResponse.data);
        const translationsData = Array.isArray(translationsResponse.data)
          ? translationsResponse.data
          : translationsResponse.data.translations || [];
        setTranslations(translationsData);

        // Process approvals data
        if (approvalsResponse.data.success) {
          setApprovalRequests(approvalsResponse.data.approvalRequests || []);
        } else {
          setApprovalRequests([]);
        }
      } catch (error) {
        console.error('Error fetching project details:', error);
        const isAuthError = error.response?.status === 401 || error.response?.status === 403;
        if (isAuthError) {
          toast.error(t('dashboard.messages.sessionExpired'));
        } else {
          toast.error(t('projectDetails.messages.updateFailed'));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProjectDetails();
  }, [projectId, apiClient]);

  // Fetch comments for all completed translations with Google Docs
  useEffect(() => {
    if (!googleAccessToken || !translations || translations.length === 0) return;

    const fetchAllComments = async () => {
      const googleDocs = new GoogleDocsService(googleAccessToken);
      const commentsMap = {};
      const loadingMap = {};

      for (const translation of translations) {
        if (translation.googleDocId && translation.status === 'COMPLETED') {
          try {
            loadingMap[translation.googleDocId] = true;
            setLoadingComments({...loadingMap});

            const comments = await googleDocs.getOpenComments(translation.googleDocId);
            commentsMap[translation.googleDocId] = comments;

            loadingMap[translation.googleDocId] = false;
          } catch (error) {
            console.error(`Error fetching comments for ${translation.googleDocId}:`, error);
            commentsMap[translation.googleDocId] = [];
            loadingMap[translation.googleDocId] = false;
          }
        }
      }

      setDocumentComments(commentsMap);
      setLoadingComments(loadingMap);
    };

    fetchAllComments();
  }, [translations, googleAccessToken]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isEditing) {
        handleCancelEdit();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isEditing]);

  const handleDocumentPreview = async (translation, viewType = 'final') => {
    try {
      const response = await apiClient.get(`/documents/${translation.documentId}/status`);
      const documentData = response.data;
      
      let documentUrl = null;
      let documentTitle = translation.originalFileName || t('common.unknownDocument');
      
      if (viewType === 'original') {
        // Preview original document
        if (documentData.dropbox && documentData.dropbox.originalUrl) {
          documentUrl = documentData.dropbox.originalUrl;
          documentTitle = `${t('projectDetails.documentLabels.original')}: ${documentTitle}`;
        } else {
          // Fallback to direct document endpoint
          documentUrl = `/documents/${translation.documentId}/preview`;
          documentTitle = `${t('projectDetails.documentLabels.original')}: ${documentTitle}`;
        }
      } else if (viewType === 'final') {
        // Preview final/translated document
        if (translation.status === 'COMPLETED' && documentData.dropbox && documentData.dropbox.hasFinalPdf) {
          documentUrl = documentData.dropbox.downloadUrl;
          documentTitle = `${t('projectDetails.documentLabels.translated')}: ${documentTitle}`;
        } else {
          toast.info(t('projectDetails.messages.finalDocNotReady'));
          return;
        }
      }
      
      if (documentUrl) {
        setSelectedDocument({
          ...translation,
          documentUrl,
          documentTitle,
          viewType,
          documentType: translation.originalFileName ? 
            translation.originalFileName.split('.').pop().toLowerCase() : 'pdf'
        });
      } else {
        toast.error(t('projectDetails.messages.previewNotAvailable'));
      }
    } catch (error) {
      console.error('Error fetching document for preview:', error);
      toast.error(t('projectDetails.messages.loadPreviewFailed'));
    }
  };

  const handleTranslationRowClick = (translation) => {
    if (translation.status === 'PENDING') {
      navigate(`/translator/setup/${translation.documentId}?projectId=${translation.projectId}`);
    } else if (translation.status === 'COMPLETED') {
      navigate(`/translator/edit/${translation.documentId}?mode=googledocs`);
    }
  };

  const handleRetryTranslation = async (translation, e) => {
    e.stopPropagation();

    try {
      setRetryingTranslations(prev => new Set(prev).add(translation.documentId));

      const response = await apiClient.post(`/translations/${translation.documentId}/retry`);

      if (response.data.success) {
        toast.success(response.data.message || 'Translation retry started successfully');

        // Refresh translations after a short delay to show updated status
        setTimeout(async () => {
          try {
            const translationsResponse = await apiClient.get(`/translations?projectId=${projectId}`);
            const translationsData = Array.isArray(translationsResponse.data)
              ? translationsResponse.data
              : translationsResponse.data.translations || [];
            setTranslations(translationsData);
          } catch (error) {
            console.error('Error refreshing translations:', error);
          } finally {
            setRetryingTranslations(prev => {
              const newSet = new Set(prev);
              newSet.delete(translation.documentId);
              return newSet;
            });
          }
        }, 1000);
      }
    } catch (error) {
      console.error('Error retrying translation:', error);

      const errorMessage = error.response?.data?.error || 'Failed to retry translation';
      const pagesExceeded = error.response?.data?.pagesExceededRetries;

      if (pagesExceeded && pagesExceeded.length > 0) {
        const pageNumbers = pagesExceeded.map(p => p.pageNumber).join(', ');
        toast.error(`Pages ${pageNumbers} have exceeded maximum retry attempts`);
      } else {
        toast.error(errorMessage);
      }

      setRetryingTranslations(prev => {
        const newSet = new Set(prev);
        newSet.delete(translation.documentId);
        return newSet;
      });
    }
  };

  const handleRemoveDocument = async (documentId, e) => {
    e.stopPropagation();

    if (!window.confirm(t('projectDetails.messages.confirmRemoveDocument'))) {
      return;
    }

    try {
      await apiClient.delete(`/document/${projectId}/${documentId}`);
      toast.success(t('projectDetails.messages.documentRemovedSuccess'));

      // Refresh project details to update document list
      const projectResponse = await apiClient.get(`/projects/${projectId}`);
      const projectData = projectResponse.data.project || projectResponse.data;
      setProject(projectData);

      // Refresh translations list as well
      const translationsResponse = await apiClient.get(`/translations?projectId=${projectId}`);
      const translationsData = Array.isArray(translationsResponse.data)
        ? translationsResponse.data
        : translationsResponse.data.translations || [];
      setTranslations(translationsData);
    } catch (error) {
      console.error('Error removing document:', error);
      const errorMessage = error.response?.data?.error || 'Failed to remove document';
      toast.error(errorMessage);
    }
  };

  // Handle delete translation
  const handleDeleteTranslation = (translation, e) => {
    e.stopPropagation();
    setDeleteDialog({
      isOpen: true,
      translationId: translation.documentId,
      translationName: translation.originalFileName || 'this translation'
    });
  };

  const confirmDeleteTranslation = async () => {
    try {
      setIsDeleting(true);
      await translationApi.deleteTranslation(apiClient, deleteDialog.translationId);

      toast.success(t('projectDetails.messages.deleteSuccess'));

      // Remove translation from local state
      setTranslations(prevTranslations =>
        prevTranslations.filter(t => t.documentId !== deleteDialog.translationId)
      );

      // Close dialog
      setDeleteDialog({ isOpen: false, translationId: null, translationName: '' });

      // Optionally refresh project to update counts
      const projectResponse = await apiClient.get(`/projects/${projectId}`);
      const projectData = projectResponse.data.project || projectResponse.data;
      setProject(projectData);
    } catch (error) {
      console.error('Error deleting translation:', error);
      const errorMessage = error.response?.data?.error || 'Failed to delete translation';
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDeleteTranslation = () => {
    setDeleteDialog({ isOpen: false, translationId: null, translationName: '' });
  };

  // Handle approval request (now for the entire project with Google Docs)
  const handleRequestApproval = () => {
    // Check if there are any completed translations with Google Docs
    const completedTranslationsWithGoogleDocs = translations.filter(
      t => t.status === 'COMPLETED' && t.googleDocId
    );

    if (completedTranslationsWithGoogleDocs.length === 0) {
      toast.error(t('projectDetails.messages.noCompletedGoogleDocs'));
      return;
    }

    // Check if Google auth token is available
    if (!googleAccessToken) {
      toast.error(t('projectDetails.messages.signInGoogleFirst'));
      return;
    }

    // Reset progress and errors
    setSharingProgress({ current: 0, total: 0 });
    setSharingErrors([]);

    setApprovalDialog({
      isOpen: true,
      projectId: projectId,
      projectName: project.name
    });
    setApprovalForm({
      approverName: '',
      approverEmail: '',
      approverLanguage: 'fr'
    });
  };

  const handleApprovalFormChange = (e) => {
    const { name, value } = e.target;
    setApprovalForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const submitApprovalRequest = async () => {
    // Validate form
    if (!approvalForm.approverName.trim()) {
      toast.error(t('projectDetails.messages.enterApproverName'));
      return;
    }
    if (!approvalForm.approverEmail.trim()) {
      toast.error(t('projectDetails.messages.enterApproverEmail'));
      return;
    }
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(approvalForm.approverEmail)) {
      toast.error(t('projectDetails.messages.enterValidEmail'));
      return;
    }

    const approverEmailTrimmed = approvalForm.approverEmail.trim();
    setIsSendingApproval(true);
    setSharingErrors([]);

    // Get completed translations with Google Docs
    const translationsToProcess = translations.filter(
      t => t.status === 'COMPLETED' && t.googleDocId
    );

    try {
      const googleDocs = new GoogleDocsService(googleAccessToken);
      const sharedTranslations = [];
      const errors = [];
      let googleSentEmail = false;

      // Initialize progress
      setSharingProgress({ current: 0, total: translationsToProcess.length });

      // Step 1: Share all Google Docs with the approver
      for (let i = 0; i < translationsToProcess.length; i++) {
        const translation = translationsToProcess[i];

        try {
          console.log(`Attempting to share document ${i + 1}/${translationsToProcess.length} (${translation.googleDocId})...`);

          let shareResult = null;

          // Try to share without Google's notification. If Google requires notification,
          // retry with notification so non-Google accounts can access.
          try {
            shareResult = await googleDocs.shareDocument(
              translation.googleDocId,
              approverEmailTrimmed,
              'commenter',
              false
            );
            console.log(`✓ Shared document ${i + 1} without Google notification`);
            console.log('Share Result:', {
              permissionId: shareResult?.permissionId,
              sharingUrl: shareResult?.sharingUrl,
              webViewLink: shareResult?.webViewLink,
              resourceKey: shareResult?.resourceKey
            });
            sharedTranslations.push({
              ...translation,
              sharingUrl: shareResult?.sharingUrl || shareResult?.webViewLink
            });
          } catch (shareError) {
            if (shareError.message?.includes('Notify people') ||
                shareError.response?.data?.error?.message?.includes('Notify people') ||
                shareError.response?.status === 400) {
              console.log(`Email requires Google notification for document ${i + 1}, retrying...`);
              shareResult = await googleDocs.shareDocument(
                translation.googleDocId,
                approverEmailTrimmed,
                'commenter',
                true
              );
              googleSentEmail = true;
              console.log(`✓ Shared document ${i + 1} with Google notification`);
              console.log('Share Result:', {
                permissionId: shareResult?.permissionId,
                sharingUrl: shareResult?.sharingUrl,
                webViewLink: shareResult?.webViewLink,
                resourceKey: shareResult?.resourceKey
              });
              sharedTranslations.push({
                ...translation,
                sharingUrl: shareResult?.sharingUrl || shareResult?.webViewLink
              });
            } else {
              throw shareError;
            }
          }
        } catch (error) {
          console.error(`Failed to share document ${i + 1}:`, error);
          errors.push({
            translationId: translation.id,
            googleDocId: translation.googleDocId,
            fileName: translation.originalFileName,
            error: error.message
          });
        }

        // Update progress
        setSharingProgress({ current: i + 1, total: translationsToProcess.length });
      }

      // Update sharing errors state
      setSharingErrors(errors);

      // If no documents were shared successfully, abort
      if (sharedTranslations.length === 0) {
        toast.error(t('projectDetails.messages.shareDocumentsFailed'));
        return;
      }

      // Step 2: Create approval request(s) via backend
      const pdfReadyTranslations = [];

      // Step 2: Export Google Docs to PDF and upload to Dropbox via backend
      for (let i = 0; i < sharedTranslations.length; i++) {
        const translation = sharedTranslations[i];

        try {
          let googleDocTitle = null;
          try {
            const metadata = await googleDocs.getFileMetadata(translation.googleDocId);
            googleDocTitle = metadata?.name || null;
          } catch (metadataError) {
            console.warn(`Could not fetch live Google title for ${translation.googleDocId}:`, metadataError.message);
          }

          const pdfBlob = await googleDocs.exportToPdf(translation.googleDocId);
          const baseName = translation.originalFileName
            ? translation.originalFileName.replace(/\.[^/.]+$/, '')
            : `translation_${translation.id}`;

          const formData = new FormData();
          formData.append('translationId', translation.id);
          if (googleDocTitle) {
            formData.append('googleDocTitle', googleDocTitle);
          }
          formData.append('file', pdfBlob, `${baseName}_approval.pdf`);

          const uploadResponse = await apiClient.post('/approvals/upload-pdf', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });

          if (!uploadResponse.data?.success) {
            throw new Error(uploadResponse.data?.error || 'Failed to upload approval PDF');
          }

          pdfReadyTranslations.push({
            ...translation,
            approvalPdfPath: uploadResponse.data.dropboxPath,
            googleDocTitle
          });
        } catch (error) {
          console.error(`Failed to export/upload PDF for ${translation.originalFileName}:`, error);
          errors.push({
            translationId: translation.id,
            googleDocId: translation.googleDocId,
            fileName: translation.originalFileName,
            error: error.message
          });
        }
      }

      if (pdfReadyTranslations.length === 0) {
        toast.error(t('projectDetails.messages.exportPdfFailed'));
        return;
      }

      // Step 3: Create approval request(s) via backend
    const response = await apiClient.post('/approvals', {
      translations: pdfReadyTranslations.map(t => ({
        translationId: t.id,
        googleDocId: t.googleDocId,
        webViewLink: t.sharingUrl,
        approvalPdfPath: t.approvalPdfPath,
        googleDocTitle: t.googleDocTitle || null
      })),
      approverName: approvalForm.approverName.trim(),
      approverEmail: approvalForm.approverEmail.trim(),
      language: approvalForm.approverLanguage,
      translatorName: userProfile?.name || user?.name || ''
    });

      if (response.data.success) {
        // Extract approval link from the first result
        const firstResult = response.data.results?.[0];
        const approvalLink = firstResult?.approvalLink || null;

        if (approvalLink) {
          console.log('📋 Client Approval Link:', approvalLink);
          console.log('👉 Copy this link and send to client:', approvalForm.approverEmail);
        }

        // Construct success message using response summary
        const summary = response.data.summary;
        let successMessage = '';

        if (summary.succeeded === translationsToProcess.length) {
          // All succeeded
          successMessage = `Approval requests sent for ${summary.succeeded} translation${summary.succeeded > 1 ? 's' : ''}! `;
        } else {
          // Partial success
          successMessage = `Approval requests sent for ${summary.succeeded} of ${summary.total} translations. ${summary.failed} failed. `;
        }

        // Add Google email note
        if (googleSentEmail) {
          successMessage += 'Note: Client will receive 2 emails (one from Google for document access, one from us with the approval link).';
        } else {
          successMessage += 'Client will receive our custom approval email.';
        }

        // Show warning if there were partial failures
        if (errors.length > 0 || summary.failed > 0) {
          toast.warning(successMessage);
        } else {
          toast.success(successMessage);
        }

        // Update approval requests state directly from response
        // Create a pseudo approval request object from the results
        if (response.data.results && response.data.results.length > 0) {
          const newApprovalRequest = {
            id: firstResult.approval.id,
            approverName: firstResult.approval.approverName,
            approverEmail: firstResult.approval.approverEmail,
            status: firstResult.approval.status,
            expiresAt: firstResult.approval.expiresAt,
            createdAt: firstResult.approval.createdAt,
            updatedAt: firstResult.approval.createdAt,
            translationApprovals: response.data.results.map(result => ({
              id: result.approval.id,
              translationId: result.approval.translationId,
              status: result.approval.status,
              approvedAt: null,
              reviewComment: null,
              translation: translations.find(t => t.id === result.approval.translationId)
            }))
          };

          // Add the new approval request to the list
          setApprovalRequests(prev => [newApprovalRequest, ...prev]);
        }

        // Close dialog and reset form
        setApprovalDialog({ isOpen: false, projectId: null, projectName: '' });
        setApprovalForm({ approverName: '', approverEmail: '' });
        setSharingProgress({ current: 0, total: 0 });
      } else {
        throw new Error(response.data.error || 'Failed to create approval request');
      }
    } catch (error) {
      console.error('Error creating approval request:', error);
      toast.error(`Failed to create approval request: ${error.response?.data?.error || error.message}`);
    } finally {
      setIsSendingApproval(false);
    }
  };

  const cancelApprovalRequest = () => {
    setApprovalDialog({ isOpen: false, projectId: null, projectName: '' });
    setApprovalForm({ approverName: '', approverEmail: '', approverLanguage: 'fr' });
  };

  const proceedToSignatureView = () => {
    setSignWarningDialog({ isOpen: false, pendingApprovals: 0, openComments: 0 });
    setSelectedSigningTranslation(null);
    setShowSignatureView(true);
  };

  const cancelSignWarning = () => {
    setSignWarningDialog({ isOpen: false, pendingApprovals: 0, openComments: 0 });
  };

  const handleSignTranslation = (translation, e) => {
    if (e) e.stopPropagation();
    if (!translation || translation.status !== 'COMPLETED' || !translation.googleDocId) {
      toast.error(t('projectDetails.messages.finalDocNotReady', 'The translated final document is not ready yet'));
      return;
    }
    setSelectedSigningTranslation(translation);
    setShowSignatureView(true);
  };

  const handleSaveSignedDocument = async ({ blob, fileName, integrityHash, certificateConfig = null }) => {
    try {
      const docSvc = documentService(apiClient);
      let googleDocTitle = null;
      try {
        const candidateTranslation = selectedSigningTranslation || translations.find(tr => tr.status === 'COMPLETED' && tr.googleDocId);
        if (candidateTranslation?.googleDocId && googleAccessToken) {
          const googleDocs = new GoogleDocsService(googleAccessToken);
          const metadata = await googleDocs.getFileMetadata(candidateTranslation.googleDocId);
          googleDocTitle = metadata?.name || null;
        }
      } catch (metadataError) {
        console.warn('Could not fetch Google Doc title for final signed file naming:', metadataError.message);
      }

      // USB mode: blob is already signed client-side — just store it.
      // .p12 mode: backend performs the signing.
      const isP12Mode = Boolean(
        certificateConfig?.enabled &&
        !certificateConfig?.usbMode &&
        certificateConfig?.certificateFile
      );

      let data;
      if (isP12Mode) {
        // ── Software .p12 signing: server-side ───────────────────────────────
        const options = {
          documentHash: integrityHash,
          signerDisplayName: certificateConfig.signerDisplayName || undefined,
          signerContactInfo: certificateConfig.signerContactInfo || undefined,
          signerTitle: certificateConfig.signerTitle || undefined,
          signatureReason: certificateConfig.signatureReason || undefined,
          signatureLocation: certificateConfig.signatureLocation || undefined,
          certificateAnchor: certificateConfig.certificateAnchor || undefined,
          certificateAppearance: certificateConfig.certificateAppearance || undefined,
          googleDocTitle,
        };
        data = selectedSigningTranslation
          ? await docSvc.uploadTranslationFinalDocumentWithCertificate(
              projectId,
              selectedSigningTranslation.id,
              blob,
              fileName,
              certificateConfig.certificateFile,
              certificateConfig.certificatePassword || '',
              options
            )
          : await docSvc.uploadFinalDocumentWithCertificate(
              projectId,
              blob,
              fileName,
              certificateConfig.certificateFile,
              certificateConfig.certificatePassword || '',
              options
            );
      } else {
        // USB mode (already signed) or no certificate — plain upload
        data = selectedSigningTranslation
          ? await docSvc.uploadTranslationFinalDocument(projectId, selectedSigningTranslation.id, blob, fileName, integrityHash, googleDocTitle)
          : await docSvc.uploadFinalDocument(projectId, blob, fileName, integrityHash, googleDocTitle);
      }
      if (selectedSigningTranslation) {
        const updatedFinalDocument = data.finalDocument;
        const updatedFinalDocuments = [
          updatedFinalDocument,
          ...(selectedSigningTranslation.finalDocuments || []).filter(doc => doc.id !== updatedFinalDocument.id)
        ];
        setTranslations(prev => prev.map((translation) => (
          translation.id === selectedSigningTranslation.id
            ? { ...translation, latestFinalDocument: updatedFinalDocument, finalDocuments: updatedFinalDocuments }
            : translation
        )));
        setProject(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            translations: (prev.translations || []).map((translation) => (
              translation.id === selectedSigningTranslation.id
                ? { ...translation, latestFinalDocument: updatedFinalDocument, finalDocuments: updatedFinalDocuments }
                : translation
            )),
          };
        });
        // Keep the signature modal open: feeding it the updated finalDocuments list
        // makes it auto-switch to its built-in "Signed Versions" view for the new version.
        setSelectedSigningTranslation(prev => (
          prev ? { ...prev, latestFinalDocument: updatedFinalDocument, finalDocuments: updatedFinalDocuments } : prev
        ));
      } else {
        setShowSignatureView(false);
        setRecentFinalDocument(data.finalDocument);
        setProject(prev => {
          if (!prev) return prev;
          const currentFinalDocs = Array.isArray(prev.finalDocuments) ? prev.finalDocuments : [];
          const merged = [data.finalDocument, ...currentFinalDocs.filter(doc => doc.version !== data.finalDocument.version)];
          merged.sort((a, b) => (b.version || 0) - (a.version || 0));
          return { ...prev, finalDocuments: merged };
        });
      }
      toast.success(t('projectDetails.messages.documentSignedSuccess'));
    } catch (error) {
      console.error('Error uploading final document:', error);
      toast.error(t('projectDetails.messages.documentSignedError', 'Failed to upload the signed document. Please try again.'));
    }
  };

  const handleCloseFinalDocument = () => {
    if (finalDocument?.blobUrl) {
      URL.revokeObjectURL(finalDocument.blobUrl);
    }
    setFinalDocument(null);
  };

  const handleViewFinalDocument = async (documentToView = recentFinalDocument) => {
    if (!documentToView) return;

    if (finalDocument?.blobUrl) {
      URL.revokeObjectURL(finalDocument.blobUrl);
      setFinalDocument(null);
    }

    setLoadingFinalPreview(true);
    try {
      const downloadUrl = documentToView.downloadUrl;
      if (!downloadUrl) {
        throw new Error('Final document download URL is missing.');
      }

      const signedPdfPath = downloadUrl.replace(/^\/api/, '');
      const response = await apiClient.get(signedPdfPath, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(response.data);
      setFinalDocument({ ...documentToView, blobUrl });
      setRecentFinalDocument(null);
    } catch (error) {
      console.error('Error loading final document preview:', error);
      toast.error(t('finalDocumentViewer.loadError', 'Failed to load the final PDF preview. Please try downloading it instead.'));
    } finally {
      setLoadingFinalPreview(false);
    }
  };

  const handleSendToClientFieldChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'selectedSignedDocumentIds') {
      setSelectedSignedDocumentIds(prev => (
        checked
          ? [...new Set([...prev, value])]
          : prev.filter(id => id !== value)
      ));
      return;
    }
    setSendToClientForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSendToClientFilesChange = (files) => {
    setSendToClientFiles(files);
    setSendToClientUploadError('');
    if (files && files.length > 0) {
      setSendToClientForm(prev => ({ ...prev, includeDocuments: true }));
    }
  };

  const openSendToClientModal = (version = null) => {
    setSendToClientTargetVersion(version);
    setSendToClientFiles([]);
    setSendToClientUploadError('');
    setSelectedSignedDocumentIds(signedDocumentOptions.map(option => option.id));
    setSendToClientForm(prev => ({
      ...prev,
      approverName: prev.approverName || project?.clientName || '',
      includeCombinedFinal: Boolean(version),
    }));
    setShowSendToClientModal(true);
  };

  const handleSendToClient = async () => {
    if (!sendToClientForm.approverEmail.trim()) return;
    setIsSendingToClient(true);
    setSendToClientUploadError('');
    const docSvc = documentService(apiClient);
    let uploadedDocuments = [];

    if (sendToClientFiles.length > 0) {
      try {
        const uploadResponse = await docSvc.uploadProjectDocuments(projectId, sendToClientFiles);
        if (!uploadResponse?.success) {
          throw new Error(uploadResponse?.error || t('projectDetails.messages.additionalDocumentsUploadFailed', 'Failed to upload additional documents'));
        }

        uploadedDocuments = uploadResponse?.documents || [];
        if (uploadedDocuments.length > 0) {
          setProject(prev => {
            if (!prev) return prev;
            const existing = Array.isArray(prev.documents) ? prev.documents : [];
            const merged = [
              ...uploadedDocuments,
              ...existing.filter(doc => !uploadedDocuments.some(uploaded => uploaded.id === doc.id))
            ];
            return { ...prev, documents: merged };
          });
        }

        setSendToClientFiles([]);
      } catch (error) {
        const uploadError = error?.response?.data?.error
          || error?.message
          || t('projectDetails.messages.additionalDocumentsUploadFailed', 'Failed to upload additional documents');
        setSendToClientUploadError(uploadError);
        toast.error(uploadError);
        setIsSendingToClient(false);
        return;
      }
    }

    try {
      const response = await docSvc.sendFinalDocumentToClient(
        projectId,
        sendToClientForm.approverEmail.trim(),
        sendToClientForm.approverLanguage,
        sendToClientForm.includeDocuments,
        sendToClientTargetVersion,
        {
          includeCombinedFinal: sendToClientForm.includeCombinedFinal,
          translationFinalDocumentIds: selectedSignedDocumentIds,
        }
      );
      const sentVersion = response?.details?.documentVersion || sendToClientTargetVersion;
      const sentAt = response?.details?.sentAt || new Date().toISOString();

      if (sentVersion) {
        setProject(prev => {
          if (!prev) return prev;
          const finalDocuments = (prev.finalDocuments || []).map(doc =>
            doc.version === sentVersion
              ? { ...doc, sentAt }
              : doc
          );
          return { ...prev, finalDocuments };
        });

        setFinalDocument(prev =>
          prev && prev.version === sentVersion
            ? { ...prev, sentAt }
            : prev
        );
      }

      if (selectedSignedDocumentIds.length > 0) {
        setTranslations(prev => prev.map((translation) => {
          const latest = translation.latestFinalDocument || translation.finalDocuments?.[0] || null;
          if (!latest || !selectedSignedDocumentIds.includes(latest.id)) return translation;
          const updatedLatest = { ...latest, sentAt };
          return {
            ...translation,
            latestFinalDocument: updatedLatest,
            finalDocuments: (translation.finalDocuments || []).map(doc => doc.id === latest.id ? updatedLatest : doc),
          };
        }));
      }

      setShowSendToClientModal(false);
      setSendToClientTargetVersion(null);
      setSelectedSignedDocumentIds([]);
      setSendToClientFiles([]);
      setSendToClientUploadError('');
      setSendToClientForm({ approverName: '', approverEmail: '', approverLanguage: 'fr', includeDocuments: false, includeCombinedFinal: false });
      toast.success(t('projectDetails.messages.finalDocumentSent', `Document sent to ${sendToClientForm.approverEmail.trim()}`));
    } catch (error) {
      console.error('Error sending final document to client:', error);
      toast.error(t('projectDetails.messages.finalDocumentSentError', 'Failed to send the document. Please try again.'));
      if (uploadedDocuments.length > 0) {
        toast.info(t('projectDetails.messages.additionalDocumentsSavedRetrySend', 'Additional uploaded documents were saved to the project. You can retry sending now.'));
      }
    } finally {
      setIsSendingToClient(false);
    }
  };

  // Get overall approval status for the project
  const getOverallApprovalStatus = () => {
    if (!approvalRequests || approvalRequests.length === 0) {
      return null;
    }

    // Get the most recent approval request
    const sortedRequests = [...approvalRequests].sort((a, b) =>
      new Date(b.createdAt) - new Date(a.createdAt)
    );
    const latestRequest = sortedRequests[0];

    return {
      status: latestRequest.status,
      approverName: latestRequest.approverName,
      approverEmail: latestRequest.approverEmail,
      expiresAt: latestRequest.expiresAt,
      updatedAt: latestRequest.updatedAt
    };
  };

  // Get approval status badge
  const getApprovalStatusBadge = (status) => {
    const statusConfig = {
      'PENDING': { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: '⏳', label: t('projectDetails.statusLabels.pendingReview') },
      'APPROVED': {
        bg: 'bg-green-100',
        text: 'text-green-800',
        icon: <img src="/approved.svg" alt="" aria-hidden="true" className="w-4 h-4 object-contain" />,
        label: t('projectDetails.statusLabels.approved')
      },
      'CHANGES_REQUESTED': { bg: 'bg-orange-100', text: 'text-orange-800', icon: '✏️', label: t('projectDetails.statusLabels.changesRequested') },
      'EXPIRED': { bg: 'bg-gray-100', text: 'text-gray-800', icon: '⌛', label: t('projectDetails.statusLabels.expired') }
    };

    const config = statusConfig[status] || statusConfig['PENDING'];

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-full ${config.bg} ${config.text}`}>
        <span>{config.icon}</span>
        {config.label}
      </span>
    );
  };

  // Get approval status for a specific translation
  const getTranslationApprovalStatus = (translationId) => {
    if (!approvalRequests || approvalRequests.length === 0) {
      return null;
    }

    // Find the most recent approval request
    const sortedRequests = [...approvalRequests].sort((a, b) =>
      new Date(b.createdAt) - new Date(a.createdAt)
    );

    // Look through approval requests to find this translation
    for (const request of sortedRequests) {
      if (request.translationApprovals) {
        const approval = request.translationApprovals.find(
          (a) => a.translationId === translationId
        );
        if (approval) {
          return {
            status: approval.status,
            reviewComment: approval.reviewComment,
            approvedAt: approval.approvedAt,
            approverName: request.approverName
          };
        }
      }
    }

    return null;
  };

  // Determine if approval column should be shown
  const shouldShowApprovalColumn = () => {
    // Show if any approval requests exist
    if (approvalRequests && approvalRequests.length > 0) {
      return true;
    }

    // Show if any translations are completed (approval-ready)
    if (translations && translations.some(t => t.status === 'COMPLETED')) {
      return true;
    }

    return false;
  };

  // Handle approval icon click
  const handleApprovalIconClick = (translationId, e) => {
    e.stopPropagation();
    const approvalStatus = getTranslationApprovalStatus(translationId);
    if (approvalStatus && (approvalStatus.reviewComment || approvalStatus.status === 'APPROVED')) {
      setSelectedApprovalComment({
        status: approvalStatus.status,
        comment: approvalStatus.reviewComment,
        approverName: approvalStatus.approverName,
        approvedAt: approvalStatus.approvedAt
      });
    }
  };

  // Handle show comments modal
  const handleShowComments = (translation, e) => {
    e.stopPropagation();

    if (!translation.googleDocId) {
      toast.error(t('projectDetails.messages.noGoogleDocAssociated'));
      return;
    }

    const comments = documentComments[translation.googleDocId] || [];
    setSelectedDocumentComments({
      translation,
      comments
    });
  };

  const handleEditProject = () => {
    // Set defaults if fields are empty
    const defaultDueDate = project.dueDate 
      ? project.dueDate.split('T')[0] 
      : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]; // 30 days from now

    setEditForm({
      name: project.name || '',
      description: project.description || '',
      clientName: project.clientName || '',
      translatorName: project.translatorName || '',
      country: project.country || 'France',
      dueDate: defaultDueDate,
      sourceLanguage: project.sourceLanguage || 'fr',
      targetLanguage: project.targetLanguage || 'en'
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm({
      name: project.name || '',
      description: project.description || '',
      clientName: project.clientName || '',
      translatorName: project.translatorName || '',
      country: project.country || '',
      dueDate: project.dueDate ? project.dueDate.split('T')[0] : '',
      sourceLanguage: project.sourceLanguage || '',
      targetLanguage: project.targetLanguage || ''
    });
  };

  const handleSaveProject = async () => {
    try {
      const response = await apiClient.put(`/projects/${projectId}`, {
        name: editForm.name,
        description: editForm.description,
        clientName: editForm.clientName,
        translatorName: editForm.translatorName,
        country: editForm.country,
        dueDate: editForm.dueDate,
        sourceLanguage: editForm.sourceLanguage,
        targetLanguage: editForm.targetLanguage
      });
      
      const updatedProject = response.data.project || response.data;
      setProject(updatedProject);
      setIsEditing(false);
      toast.success(t('projectDetails.messages.updateSuccess'));
    } catch (error) {
      console.error('Error updating project:', error);
      toast.error(t('projectDetails.messages.updateFailed'));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'PENDING': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: t('projectDetails.statusLabels.pending') },
      'PROCESSING': { bg: 'bg-blue-100', text: 'text-blue-800', label: t('projectDetails.statusLabels.processing') },
      'COMPLETED': { bg: 'bg-green-100', text: 'text-green-800', label: t('projectDetails.statusLabels.completed') },
      'FAILED': { bg: 'bg-red-100', text: 'text-red-800', label: t('projectDetails.statusLabels.failed') }
    };
    
    const config = statusConfig[status] || statusConfig['PENDING'];
    
    return (
      <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getDueDateStatus = () => {
    if (!project.dueDate) return null;
    
    const now = new Date();
    const dueDate = new Date(project.dueDate);
    const diffTime = dueDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    // Check if all translations are completed
    const allCompleted = translations.length > 0 && translations.every(t => t.status === 'COMPLETED');
    
    if (allCompleted) {
      return { status: 'completed', className: 'text-green-600', message: t('projectDetails.statusLabels.allCompleted') };
    }

    if (diffDays < 0) {
      return { status: 'overdue', className: 'text-red-600 font-semibold', message: t('projectDetails.statusLabels.overdue') };
    } else if (diffDays <= 2) {
      return { status: 'urgent', className: 'text-red-500 font-semibold', message: t('projectDetails.statusLabels.dueSoon') };
    } else if (diffDays <= 7) {
      return { status: 'warning', className: 'text-orange-500', message: t('projectDetails.statusLabels.dueThisWeek') };
    }
    
    return { status: 'normal', className: 'text-gray-700', message: null };
  };

  if (loading) {
    return <Spinner />;
  }

  if (!project) {
    return (
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-2xl font-semibold text-gray-800 mb-4">{t('projectDetails.messages.projectNotFound')}</h1>
        <Link to="/translator/dashboard" className="text-blue-600 hover:text-blue-800">
          ← {t('projectDetails.back')}
        </Link>
      </div>
    );
  }

  return (
    <div className="-mx-4 px-4 sm:px-6 lg:px-8">
      <div className="w-full bg-white rounded-lg shadow-lg p-3 sm:p-4 md:p-6">
      {/* Header */}
      <div className="mb-4">

        <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4 mb-4">
          <div className="flex-1">
            <div>
              <h1 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">{project.name}</h1>
              <p className="text-sm sm:text-base text-gray-600 mb-3 whitespace-pre-line">{project.description}</p>
              <div className="flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm">
                {project.clientName && (
                  <div className="flex items-center gap-1">
                    <span className="text-gray-500">👤</span>
                    <span className="text-gray-700">{t('projectDetails.info.clientName')}: {project.clientName}</span>
                  </div>
                )}
                {project.translatorName && (
                  <div className="flex items-center gap-1">
                    <span className="text-gray-500">🖋️</span>
                    <span className="text-gray-700">{t('projectDetails.info.translatorName')}: {project.translatorName}</span>
                  </div>
                )}
                {project.country && (
                  <div className="flex items-center gap-1">
                    <span className="text-gray-500">🌍</span>
                    <span className="text-gray-700">{project.country}</span>
                  </div>
                )}
                {project.dueDate && (
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1">
                      <span className="text-gray-500">📅</span>
                      <span className="text-gray-700">
                        {t('projectDetails.info.due')}: {new Date(project.dueDate).toLocaleDateString()}
                      </span>
                    </div>
                    {getDueDateStatus()?.message && (
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        getDueDateStatus()?.status === 'overdue' ? 'bg-red-100 text-red-700' :
                        getDueDateStatus()?.status === 'urgent' ? 'bg-red-50 text-red-600' :
                        getDueDateStatus()?.status === 'warning' ? 'bg-orange-50 text-orange-600' :
                        'bg-green-50 text-green-600'
                      }`}>
                        {getDueDateStatus()?.message}
                      </span>
                    )}
                  </div>
                )}
                {project.sourceLanguage && project.targetLanguage && (
                  <div className="flex items-center gap-1">
                    <span className="text-gray-500">🌐</span>
                    <span className="text-gray-700">
                      {project.sourceLanguage.toUpperCase()} → {project.targetLanguage.toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 md:flex-nowrap">
            <button
              onClick={handleEditProject}
              className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors font-medium shadow-sm whitespace-nowrap"
            >
              <span>✏️</span>
              <span className="hidden sm:inline">{t('projectDetails.edit')}</span>
            </button>
            <Link
              to={`/translator/setup?projectId=${projectId}`}
              className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium shadow-sm whitespace-nowrap"
            >
              <span>📤</span>
              <span className="hidden sm:inline">{t('dashboard.actions.uploadDocument')}</span>
            </Link>
            <button
              onClick={handleRequestApproval}
              className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm bg-purple-300 rounded-md hover:bg-purple-400 transition-colors font-medium shadow-sm whitespace-nowrap"
              title={approvalRequests && approvalRequests.length > 0
                ? t('projectDetails.tooltips.resendApproval')
                : t('projectDetails.tooltips.requestApproval')}
            >
              <span>✉️</span>
              <span className="hidden sm:inline">{approvalRequests && approvalRequests.length > 0 ? t('projectDetails.resendApproval') : t('projectDetails.requestApproval')}</span>
            </button>
            {hasSignedDocumentFinals && (
              <button
                onClick={() => openSendToClientModal(null)}
                className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium shadow-sm whitespace-nowrap"
                title={t('projectDetails.actions.sendToClient', 'Send to Client')}
              >
                <span>📧</span>
                <span className="hidden sm:inline">{t('projectDetails.actions.sendToClient', 'Send to Client')}</span>
              </button>
            )}
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4 text-xs sm:text-sm">
            <div>
              <span className="font-medium text-gray-700">{t('projectDetails.info.createdAt')}:</span>
              <span className="ml-2 text-gray-600">
                {new Date(project.createdAt).toLocaleDateString()}
              </span>
            </div>
            <div>
              <span className="font-medium text-gray-700">{t('projectDetails.info.translations')}:</span>
              <span className="ml-2 text-gray-600">{project?.translations?.length || 0}</span>
            </div>
            {project.country && (
              <div>
                <span className="font-medium text-gray-700">{t('projectDetails.info.country')}:</span>
                <span className="ml-2 text-gray-600">{project.country}</span>
              </div>
            )}
            {project.clientName && (
              <div>
                <span className="font-medium text-gray-700">{t('projectDetails.info.clientName')}:</span>
                <span className="ml-2 text-gray-600">{project.clientName}</span>
              </div>
            )}
            {project.translatorName && (
              <div>
                <span className="font-medium text-gray-700">{t('projectDetails.info.translatorName')}:</span>
                <span className="ml-2 text-gray-600">{project.translatorName}</span>
              </div>
            )}
            {project.dueDate && (
              <div className="flex flex-col">
                <div>
                  <span className="font-medium text-gray-700">{t('projectDetails.info.dueDate')}:</span>
                  <span className="ml-2 text-gray-600">
                    {new Date(project.dueDate).toLocaleDateString()}
                  </span>
                </div>
                {getDueDateStatus()?.message && (
                  <span className={`text-xs mt-1 ${
                    getDueDateStatus()?.status === 'overdue' ? 'text-red-600' :
                    getDueDateStatus()?.status === 'urgent' ? 'text-red-500' :
                    getDueDateStatus()?.status === 'warning' ? 'text-orange-500' :
                    'text-green-600'
                  }`}>
                    {getDueDateStatus()?.message}
                  </span>
                )}
              </div>
            )}
            {project.sourceLanguage && (
              <div>
                <span className="font-medium text-gray-700">{t('projectDetails.info.from')}:</span>
                <span className="ml-2 text-gray-600">{project.sourceLanguage.toUpperCase()}</span>
              </div>
            )}
            {project.targetLanguage && (
              <div>
                <span className="font-medium text-gray-700">{t('projectDetails.info.to')}:</span>
                <span className="ml-2 text-gray-600">{project.targetLanguage.toUpperCase()}</span>
              </div>
            )}
            <div className="flex justify-between items-center lg:col-span-full xl:col-span-6">
              <div>
                <span className="font-medium text-gray-700">{t('projectDetails.info.id')}:</span>
                <span className="ml-2 text-gray-600 font-mono text-xs">{project.id}</span>
              </div>
              {project.documents?.length > 0 ? (
                <button
                  onClick={() => setShowDocumentModal(true)}
                  className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium mr-10 bg-transparent border-none cursor-pointer"
                >
                  📄 {project.documents.length} {t('projectDetails.info.documents')}{project.documents.length !== 1 ? 's' : ''}
                </button>
              ) : (
                <Link
                  to={`/translator/setup?projectId=${projectId}&mode=upload`}
                  className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium mr-10"
                >
                  📤 {t('projectDetails.translations.uploadDocuments')}
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Translations Section */}
      <div className="mb-8">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4">{t('projectDetails.translations.title')}</h2>

        {!translations || translations.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <div className="text-4xl mb-4">🌐</div>
            <h3 className="text-base sm:text-lg font-medium text-gray-800 mb-2">{t('projectDetails.translations.noTranslations')}</h3>
            <p className="text-sm sm:text-base text-gray-600 mb-4">{t('projectDetails.translations.uploadFirst')}</p>
            <Link
              to={`/translator/setup?projectId=${projectId}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium shadow-sm"
            >
              <span>📤</span>
              {t('projectDetails.translations.startTranslation')}
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="hidden md:table w-full border-collapse bg-white rounded-lg shadow-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 border-b">{t('projectDetails.translations.document')}</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 border-b">{t('projectDetails.translations.status')}</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 border-b">{t('projectDetails.translations.progress')}</th>
                  <th className="px-6 py-3 text-center text-sm font-medium text-gray-700 border-b">{t('projectDetails.translations.comments')}</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 border-b">{t('projectDetails.translations.created')}</th>
                  {shouldShowApprovalColumn() && (
                    <th className="px-6 py-3 text-center text-sm font-medium text-gray-700 border-b">{t('projectDetails.translations.approval')}</th>
                  )}
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 border-b">{t('projectDetails.translations.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {(translations || []).map((translation) => (
                  <tr 
                    key={translation.id} 
                    className={`border-b ${
                      translation.status === 'PENDING' || translation.status === 'COMPLETED'
                        ? 'hover:bg-blue-50 cursor-pointer' 
                        : 'hover:bg-gray-50'
                    }`}
                    onClick={() => handleTranslationRowClick(translation)}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">🌐</span>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-gray-800">{translation.originalFileName || t('common.unknownDocument')}</p>
                            {translation.status === 'PENDING' && (
                              <span className="text-blue-600 text-xs">
                                ← {t('projectDetails.translations.clickToTranslate')}
                              </span>
                            )}
                            {translation.status === 'COMPLETED' && (
                              <span className="text-green-600 text-xs">
                                ← {t('projectDetails.translations.clickToEdit')}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">{t('projectDetails.translations.translationId')}: {translation.id}</p>
                          {(() => {
                            const latestFinal = translation.latestFinalDocument || translation.finalDocuments?.[0] || null;
                            if (!latestFinal) {
                              return translation.status === 'COMPLETED' && translation.googleDocId ? (
                                <p className="text-xs text-gray-500 mt-1">Not signed</p>
                              ) : null;
                            }
                            return (
                              <p className="text-xs text-green-700 mt-1">
                                ✓ Signed v{latestFinal.version}{latestFinal.sentAt ? ' · Sent' : ''}
                              </p>
                            );
                          })()}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(translation.status)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${translation.progress || 0}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-gray-500 mt-1">{translation.progress || 0}%</span>
                    </td>
                    <td className="px-6 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                      {translation.googleDocId && translation.status === 'COMPLETED' ? (
                        loadingComments[translation.googleDocId] ? (
                          <span className="text-gray-400">⏳</span>
                        ) : (
                          <button
                            onClick={(e) => handleShowComments(translation, e)}
                            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                              (documentComments[translation.googleDocId]?.length || 0) > 0
                                ? 'bg-orange-100 text-orange-800 hover:bg-orange-200'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                            title={t('projectDetails.tooltips_extra.openComments', { count: documentComments[translation.googleDocId]?.length || 0 })}
                          >
                            💬 {documentComments[translation.googleDocId]?.length || 0}
                          </button>
                        )
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {new Date(translation.createdAt).toLocaleDateString()}
                    </td>
                    {shouldShowApprovalColumn() && (
                      <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                        {(() => {
                          const approvalStatus = getTranslationApprovalStatus(translation.id);
                          const isReadyForApproval = translation.readyForApproval || translation.ready_for_approval;

                          // Show approval status icons if there's an approval request
                          if (approvalStatus && approvalStatus.status) {
                            return (
                              <div className="flex items-center justify-center gap-2">
                                {approvalStatus.status === 'PENDING' && (
                                  <button
                                    className="text-2xl hover:scale-110 transition-transform cursor-default"
                                    title={t('projectDetails.tooltips.waitingForApproval')}
                                  >
                                    🕐
                                  </button>
                                )}
                                {approvalStatus.status === 'CHANGES_REQUESTED' && (
                                  <button
                                    onClick={(e) => handleApprovalIconClick(translation.id, e)}
                                    className="text-2xl hover:scale-110 transition-transform cursor-pointer"
                                    title={t('projectDetails.tooltips.viewRequestedChanges')}
                                  >
                                    ✍️
                                  </button>
                                )}
                                {approvalStatus.status === 'APPROVED' && (
                                  <button
                                    onClick={(e) => handleApprovalIconClick(translation.id, e)}
                                    className="text-2xl hover:scale-110 transition-transform cursor-pointer"
                                    title={t('projectDetails.tooltips.viewApprovalComment')}
                                  >
                                    <img src="/approved.svg" alt="" aria-hidden="true" className="w-7 h-7 object-contain" />
                                  </button>
                                )}
                              </div>
                            );
                          }

                          // Show ready for approval icon only when no approval status exists yet
                          if (isReadyForApproval) {
                            return (
                              <div className="flex items-center justify-center gap-2">
                                <button
                                  className="text-2xl hover:scale-110 transition-transform cursor-default"
                                  title={t('projectDetails.tooltips.readyForApproval')}
                                >
                                  <ReadyForApprovalIcon size={28} />
                                </button>
                              </div>
                            );
                          }

                          // No status to show
                          return <span className="text-gray-400 text-sm flex justify-center">-</span>;
                        })()}
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <div className="flex gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
                        {/* Preview Original Document */}
                        {/* <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDocumentPreview(translation, 'original');
                          }}
                          className="px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded hover:bg-blue-200 transition-colors"
                          title="Preview original document"
                        >
                          👁️ Original
                        </button> */}

                        {/* Preview/Download Translated Document */}
                        {translation.status === 'COMPLETED' ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDocumentPreview(translation, 'final');
                            }}
                            className="px-3 py-1 text-sm bg-green-100 text-green-800 rounded hover:bg-green-200 transition-colors"
                            title={t('projectDetails.actions.viewTranslated')}
                          >
                            👁️ {t('projectDetails.actions.viewTranslated')}
                          </button>
                        ) : translation.status === 'PROCESSING' ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              //Todo add ability to edit

                              return
                                navigate(`/translator/edit/${translation.documentId}?mode=googledocs`);
                              }}
                            className="px-3 py-1 text-sm bg-orange-100 text-orange-800 rounded hover:bg-orange-200 transition-colors"
                            title={t('projectDetails.actions.processing')}
                          >
                            {t('projectDetails.actions.processing')}
                          </button>
                        ) : translation.status === 'FAILED' ? (
                          <button
                            onClick={(e) => handleRetryTranslation(translation, e)}
                            disabled={retryingTranslations.has(translation.documentId)}
                            className={`px-3 py-1 text-sm rounded transition-colors ${
                              retryingTranslations.has(translation.documentId)
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-red-100 text-red-800 hover:bg-red-200'
                            }`}
                            title={t('projectDetails.actions.retry')}
                          >
                            {retryingTranslations.has(translation.documentId) ? `⏳ ${t('projectDetails.actions.retrying')}` : `🔄 ${t('projectDetails.actions.retry')}`}
                          </button>
                        ) : (
                          <span className="px-3 py-1 text-sm bg-gray-100 text-gray-500 rounded">
                            {t('projectDetails.actions.pending')}
                          </span>
                        )}

	                        {translation.status === 'COMPLETED' && translation.googleDocId && (
	                          <button
	                            onClick={(e) => handleSignTranslation(translation, e)}
	                            className="px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded hover:bg-blue-200 transition-colors"
	                            title={translation.latestFinalDocument || translation.finalDocuments?.[0] ? 'Create a new signed version' : 'Sign this document'}
	                          >
	                            {translation.latestFinalDocument || translation.finalDocuments?.[0] ? `✍️ Re-sign v${(translation.latestFinalDocument || translation.finalDocuments?.[0]).version}` : '✍️ Sign'}
	                          </button>
	                        )}

	                        {(() => {
	                          const latestFinal = translation.latestFinalDocument || translation.finalDocuments?.[0] || null;
	                          const downloadUrl = getFinalDocumentDownloadUrl(latestFinal);
	                          if (!downloadUrl) return null;

	                          return (
	                            <a
	                              href={downloadUrl}
	                              download={latestFinal.fileName || undefined}
	                              onClick={(e) => e.stopPropagation()}
	                              className="px-3 py-1 text-sm bg-gray-100 text-gray-800 rounded hover:bg-gray-200 transition-colors"
	                              title={t('projectDetails.finalDocument.downloadPdf', 'Download')}
	                            >
	                              ⬇️ {t('projectDetails.finalDocument.downloadPdf', 'Download')}
	                            </a>
	                          );
	                        })()}

	                        {/* Delete Button */}
                        <button
                          onClick={(e) => handleDeleteTranslation(translation, e)}
                          className="px-3 py-1 text-sm bg-red-100 text-red-800 rounded hover:bg-red-200 transition-colors"
                          title={t('projectDetails.actions.delete')}
                        >
                          {t('projectDetails.actions.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-4">
              {(translations || []).map((translation) => (
                <div
                  key={translation.id}
                  className={`bg-white rounded-lg shadow-sm border p-4 ${
                    translation.status === 'PENDING' || translation.status === 'COMPLETED'
                      ? 'hover:bg-blue-50 cursor-pointer border-blue-200'
                      : 'border-gray-200'
                  }`}
                  onClick={() => handleTranslationRowClick(translation)}
                >
                  {/* Document Name */}
                  <div className="flex items-start gap-3 mb-3">
                    <span className="text-2xl">🌐</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 text-sm break-words">
                        {translation.originalFileName || t('common.unknownDocument')}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {t('projectDetails.translations.translationId')}: {translation.id}
                      </p>
                      {translation.status === 'PENDING' && (
                        <span className="text-blue-600 text-xs block mt-1">
                          ← {t('projectDetails.translations.clickToTranslate')}
                        </span>
                      )}
                      {translation.status === 'COMPLETED' && (
                        <span className="text-green-600 text-xs block mt-1">
                          ← {t('projectDetails.translations.clickToEdit')}
                        </span>
                      )}
                      {(() => {
                        const latestFinal = translation.latestFinalDocument || translation.finalDocuments?.[0] || null;
                        if (!latestFinal) {
                          return translation.status === 'COMPLETED' && translation.googleDocId ? (
                            <span className="text-gray-500 text-xs block mt-1">Not signed</span>
                          ) : null;
                        }
                        return (
                          <span className="text-green-700 text-xs block mt-1">
                            ✓ Signed v{latestFinal.version}{latestFinal.sentAt ? ' · Sent' : ''}
                          </span>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Status and Progress */}
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">{t('projectDetails.translations.status')}</p>
                      {getStatusBadge(translation.status)}
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">{t('projectDetails.translations.progress')}</p>
                      <div className="w-full bg-gray-200 rounded-full h-2 mb-1">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${translation.progress || 0}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-gray-500">{translation.progress || 0}%</span>
                    </div>
                  </div>

                  {/* Comments, Created Date, Approval */}
                  <div className="flex flex-wrap items-center gap-3 text-xs">
                    {/* Comments */}
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      {translation.googleDocId && translation.status === 'COMPLETED' ? (
                        loadingComments[translation.googleDocId] ? (
                          <span className="text-gray-400">⏳</span>
                        ) : (
                          <button
                            onClick={(e) => handleShowComments(translation, e)}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                              (documentComments[translation.googleDocId]?.length || 0) > 0
                                ? 'bg-orange-100 text-orange-800 hover:bg-orange-200'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            💬 {documentComments[translation.googleDocId]?.length || 0}
                          </button>
                        )
                      ) : (
                        <span className="text-gray-400">💬 -</span>
                      )}
                    </div>

                    {/* Created Date */}
                    <div className="flex items-center gap-1 text-gray-600">
                      <span>📅</span>
                      <span>{new Date(translation.createdAt).toLocaleDateString()}</span>
                    </div>

                    {/* Approval Status */}
                    {shouldShowApprovalColumn() && (
                      <div onClick={(e) => e.stopPropagation()}>
                        {(() => {
                          const approvalStatus = getTranslationApprovalStatus(translation.id);
                          const isReadyForApproval = translation.readyForApproval || translation.ready_for_approval;

                          if (approvalStatus && approvalStatus.status) {
                            return (
                              <div className="flex items-center gap-1">
                                {approvalStatus.status === 'PENDING' && (
                                  <button
                                    className="text-xl hover:scale-110 transition-transform cursor-default"
                                    title={t('projectDetails.tooltips.waitingForApproval')}
                                  >
                                    🕐
                                  </button>
                                )}
                                {approvalStatus.status === 'CHANGES_REQUESTED' && (
                                  <button
                                    onClick={(e) => handleApprovalIconClick(translation.id, e)}
                                    className="text-xl hover:scale-110 transition-transform cursor-pointer"
                                    title={t('projectDetails.tooltips.viewRequestedChanges')}
                                  >
                                    ✍️
                                  </button>
                                )}
                                {approvalStatus.status === 'APPROVED' && (
                                  <button
                                    onClick={(e) => handleApprovalIconClick(translation.id, e)}
                                    className="text-xl hover:scale-110 transition-transform cursor-pointer"
                                    title={t('projectDetails.tooltips.viewApprovalComment')}
                                  >
                                    <img src="/approved.svg" alt="" aria-hidden="true" className="w-6 h-6 object-contain" />
                                  </button>
                                )}
                              </div>
                            );
                          }

                          if (isReadyForApproval) {
                            return (
                              <button
                                className="text-xl hover:scale-110 transition-transform cursor-default"
                                title={t('projectDetails.tooltips.readyForApproval')}
                              >
                                <ReadyForApprovalIcon size={22} />
                              </button>
                            );
                          }

                          return null;
                        })()}
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100" onClick={(e) => e.stopPropagation()}>
                    {translation.status === 'FAILED' && (
                      <button
                        onClick={(e) => handleRetryTranslation(translation, e)}
                        className="flex items-center gap-1 px-2 py-1 text-xs bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors"
                      >
                        🔄 {t('projectDetails.actions.retry')}
                      </button>
                    )}
                    {translation.status === 'COMPLETED' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDocumentPreview(translation, 'final');
                        }}
                        className="flex items-center gap-1 px-2 py-1 text-xs bg-green-300 rounded hover:bg-green-400 transition-colors"
                      >
                        👁️ {t('projectDetails.actions.viewTranslated')}
                      </button>
                    )}
	                    {translation.status === 'COMPLETED' && translation.googleDocId && (
	                      <button
	                        onClick={(e) => handleSignTranslation(translation, e)}
	                        className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-300 rounded hover:bg-blue-400 transition-colors"
	                      >
	                        {translation.latestFinalDocument || translation.finalDocuments?.[0] ? `✍️ Re-sign v${(translation.latestFinalDocument || translation.finalDocuments?.[0]).version}` : '✍️ Sign'}
	                      </button>
	                    )}
	                    {(() => {
	                      const latestFinal = translation.latestFinalDocument || translation.finalDocuments?.[0] || null;
	                      const downloadUrl = getFinalDocumentDownloadUrl(latestFinal);
	                      if (!downloadUrl) return null;

	                      return (
	                        <a
	                          href={downloadUrl}
	                          download={latestFinal.fileName || undefined}
	                          onClick={(e) => e.stopPropagation()}
	                          className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-200 rounded hover:bg-gray-300 transition-colors"
	                          title={t('projectDetails.finalDocument.downloadPdf', 'Download')}
	                        >
	                          ⬇️ {t('projectDetails.finalDocument.downloadPdf', 'Download')}
	                        </a>
	                      );
	                    })()}
	                    <button
	                      onClick={(e) => handleDeleteTranslation(translation, e)}
	                      className="flex items-center gap-1 px-2 py-1 text-xs bg-red-300 rounded hover:bg-red-400 transition-colors"
	                    >
                      🗑️ {t('projectDetails.actions.delete')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>


      {/* Document Preview Modal */}
      {selectedDocument && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative mx-auto p-3 sm:p-5 border w-full max-w-6xl shadow-lg rounded-md bg-white max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center mb-3 sm:mb-4">
              <h3 className="text-sm sm:text-lg font-medium text-gray-900 truncate pr-2">
                {t('projectDetails.modals.documentPreview')}: {selectedDocument.documentTitle || selectedDocument.originalFileName || 'Document'}
              </h3>
              <button
                onClick={() => setSelectedDocument(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="flex-1 overflow-hidden">
              <DocumentViewer
                documentUrl={selectedDocument.documentUrl}
                title={selectedDocument.documentTitle || selectedDocument.originalFileName || 'Document'}
                documentType={selectedDocument.documentType}
                initialHeight={600}
                showPageControls={true}
              />
            </div>

            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setSelectedDocument(null)}
                className="px-3 py-1.5 text-sm bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors shadow-sm"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Project Modal */}
      {isEditing && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-4">
          <div className="relative top-4 sm:top-20 mx-auto p-4 sm:p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4 sm:mb-6">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
                {t('projectDetails.modals.editProject')}
              </h3>
              <button
                onClick={handleCancelEdit}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              {/* Project Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('projectDetails.form.projectNameRequired')}
                </label>
                <input
                  type="text"
                  name="name"
                  value={editForm.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('projectDetails.form.enterProjectName')}
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('projectDetails.form.description')}
                </label>
                <textarea
                  name="description"
                  value={editForm.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('projectDetails.form.enterDescription')}
                />
              </div>

              {/* Client and Translator Metadata */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('projectDetails.form.clientName')}
                  </label>
                  <input
                    type="text"
                    name="clientName"
                    value={editForm.clientName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('projectDetails.form.enterClientName')}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t('projectDetails.form.clientNameHint')}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('projectDetails.form.translatorName')}
                  </label>
                  <input
                    type="text"
                    name="translatorName"
                    value={editForm.translatorName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('projectDetails.form.enterTranslatorName')}
                  />
                </div>
              </div>

              {/* Country and Due Date */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('projectDetails.form.country')}
                  </label>
                  <select
                    name="country"
                    value={editForm.country}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">{t('projectDetails.form.selectCountry')}</option>
                    <option value="France">France</option>
                    <option value="United States">United States</option>
                    <option value="United Kingdom">United Kingdom</option>
                    <option value="Germany">Germany</option>
                    <option value="Spain">Spain</option>
                    <option value="Italy">Italy</option>
                    <option value="Canada">Canada</option>
                    <option value="Australia">Australia</option>
                    <option value="Netherlands">Netherlands</option>
                    <option value="Belgium">Belgium</option>
                    <option value="Switzerland">Switzerland</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('projectDetails.form.dueDate')}
                  </label>
                  <input
                    type="date"
                    name="dueDate"
                    value={editForm.dueDate}
                    onChange={handleInputChange}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Source and Target Languages */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('projectDetails.form.sourceLanguage')}
                  </label>
                  <select
                    name="sourceLanguage"
                    value={editForm.sourceLanguage}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">{t('projectDetails.form.selectSourceLanguage')}</option>
                    <option value="en">English</option>
                    <option value="fr">French</option>
                    <option value="es">Spanish</option>
                    <option value="de">German</option>
                    <option value="it">Italian</option>
                    <option value="pt">Portuguese</option>
                    <option value="nl">Dutch</option>
                    <option value="ru">Russian</option>
                    <option value="zh">Chinese</option>
                    <option value="ja">Japanese</option>
                    <option value="ko">Korean</option>
                    <option value="ar">Arabic</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('projectDetails.form.targetLanguage')}
                  </label>
                  <select
                    name="targetLanguage"
                    value={editForm.targetLanguage}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">{t('projectDetails.form.selectTargetLanguage')}</option>
                    <option value="en">English</option>
                    <option value="fr">French</option>
                    <option value="es">Spanish</option>
                    <option value="de">German</option>
                    <option value="it">Italian</option>
                    <option value="pt">Portuguese</option>
                    <option value="nl">Dutch</option>
                    <option value="ru">Russian</option>
                    <option value="zh">Chinese</option>
                    <option value="ja">Japanese</option>
                    <option value="ko">Korean</option>
                    <option value="ar">Arabic</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="mt-8 flex justify-end gap-2">
              <button
                onClick={handleCancelEdit}
                className="px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSaveProject}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium shadow-sm"
              >
                {t('projectDetails.form.saveChanges')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document List Modal */}
      {showDocumentModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-4">
          <div className="relative top-4 sm:top-10 mx-auto p-4 sm:p-6 border w-full max-w-5xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4 sm:mb-6">
              <h3 className="text-lg sm:text-2xl font-semibold text-gray-900">
                {t('projectDetails.modals.projectDocuments')}
              </h3>
              <button
                onClick={() => setShowDocumentModal(false)}
                className="text-gray-400 hover:text-gray-600 text-3xl font-light leading-none"
              >
                ×
              </button>
            </div>

            {/* Add New Document Button */}
            <div className="mb-4 flex justify-end">
              <Link
                to={`/translator/setup?projectId=${projectId}&mode=upload`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-300 rounded-md hover:bg-green-400 transition-colors font-medium shadow-sm"
              >
                <span>➕</span>
                {t('projectDetails.translations.addNewDocument')}
              </Link>
            </div>

            {/* Document List */}
            <div className="overflow-y-auto max-h-96">
              {project.documents && project.documents.length > 0 ? (
                <div className="space-y-3">
                  {project.documents.map((document) => (
                    <div
                      key={document.id}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <span className="text-3xl">📄</span>
                        <div className="flex-1">
                          <p className="font-medium text-gray-800">
                            {document.originalFileName || document.name || t('common.unknownDocument')}
                          </p>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-xs text-gray-500">
                              {t('projectDetails.translations.created')}: {new Date(document.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-2">
                        {/* View Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();

                            // Convert Dropbox URL for direct viewing
                            // Change dl=0 to raw=1 for iframe embedding
                            let documentUrl = document.dropboxUrl;
                            if (documentUrl) {
                              documentUrl = documentUrl.replace('dl=0', 'raw=1');
                            }

                            const documentTitle = document.originalFileName || document.name || 'Document';
                            const fileExtension = document.originalFileName ?
                              document.originalFileName.split('.').pop().toLowerCase() : 'pdf';

                            // Map image extensions to 'image' type for proper rendering
                            const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
                            const documentType = imageExtensions.includes(fileExtension) ? 'image' : fileExtension;

                            if (documentUrl) {
                              setSelectedDocument({
                                ...document,
                                documentUrl,
                                documentTitle,
                                documentType
                              });
                              setShowDocumentModal(false);
                            } else {
                              toast.error(t('projectDetails.messages.documentUrlNotAvailable'));
                            }
                          }}
                          className="px-3 py-1.5 text-sm bg-blue-100 text-blue-800 rounded-md hover:bg-blue-200 transition-colors font-medium"
                          title={t('projectDetails.actions.view')}
                        >
                          👁️ {t('projectDetails.actions.view')}
                        </button>

                        {/* Remove Button */}
                        <button
                          onClick={(e) => handleRemoveDocument(document.id, e)}
                          className="px-3 py-1.5 text-sm bg-red-100 text-red-800 rounded-md hover:bg-red-200 transition-colors font-medium"
                          title={t('projectDetails.actions.remove')}
                        >
                          🗑️ {t('projectDetails.actions.remove')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <div className="text-5xl mb-4">📄</div>
                  <p className="text-lg">{t('projectDetails.dialog.noDocumentsYet')}</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowDocumentModal(false)}
                className="px-3 py-1.5 text-sm bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors font-medium shadow-sm"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approval Request Modal */}
      <ApprovalRequestModal
        isOpen={approvalDialog.isOpen}
        title={t('projectDetails.modals.requestClientApproval')}
        description={t('projectDetails.approvalModal.descriptionTemplate', { projectName: approvalDialog.projectName })}
        countNote={translations.filter(t => t.status === 'COMPLETED' && t.googleDocId).length > 0
          ? `${translations.filter(t => t.status === 'COMPLETED' && t.googleDocId).length} ${t('projectDetails.dialog.completedTranslationsNote')}`
          : null}
        noteText={t('projectDetails.dialog.approvalEmailNote')}
        approverName={approvalForm.approverName}
        approverEmail={approvalForm.approverEmail}
        approverLanguage={approvalForm.approverLanguage}
        onFieldChange={handleApprovalFormChange}
        onCancel={cancelApprovalRequest}
        onSubmit={submitApprovalRequest}
        isSubmitting={isSendingApproval}
        submitLabel={`✉️ ${t('projectDetails.dialog.sendRequest')}`}
        accent="blue"
        progress={sharingProgress}
        errors={sharingErrors}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={cancelDeleteTranslation}
        onConfirm={confirmDeleteTranslation}
        title={t('projectDetails.modals.deleteTranslation')}
        message={`${t('projectDetails.dialog.deleteConfirm')} "${deleteDialog.translationName}"? ${t('projectDetails.dialog.thisAction')}`}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        isLoading={isDeleting}
      />

      {/* Approval Comment Modal */}
      {selectedApprovalComment && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative mx-auto p-4 sm:p-6 border w-full max-w-lg shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-3 sm:mb-4">
              <h3 className="text-base sm:text-xl font-semibold text-gray-900">
                {selectedApprovalComment.status === 'APPROVED' ? t('projectDetails.modals.approvalComment') : t('projectDetails.modals.requestedChanges')}
              </h3>
              <button
                onClick={() => setSelectedApprovalComment(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              {/* Status Badge */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">{t('projectDetails.table.status')}:</span>
                {getApprovalStatusBadge(selectedApprovalComment.status)}
              </div>

              {/* Approver Name */}
              {selectedApprovalComment.approverName && (
                <div>
                  <span className="text-sm font-medium text-gray-700">{t('projectDetails.status.reviewer')}:</span>
                  <span className="ml-2 text-gray-600">{selectedApprovalComment.approverName}</span>
                </div>
              )}

              {/* Approved/Reviewed Date */}
              {selectedApprovalComment.approvedAt && (
                <div>
                  <span className="text-sm font-medium text-gray-700">{t('projectDetails.status.reviewed')}:</span>
                  <span className="ml-2 text-gray-600">
                    {new Date(selectedApprovalComment.approvedAt).toLocaleString()}
                  </span>
                </div>
              )}

              {/* Comment */}
              {selectedApprovalComment.comment && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {selectedApprovalComment.status === 'APPROVED' ? `${t('projectDetails.status.comment')}:` : `${t('projectDetails.modals.requestedChanges')}:`}
                  </label>
                  <div className={`p-4 rounded-lg ${
                    selectedApprovalComment.status === 'APPROVED'
                      ? 'bg-green-50 border border-green-200'
                      : 'bg-orange-50 border border-orange-200'
                  }`}>
                    <p className="text-gray-800 whitespace-pre-wrap">{selectedApprovalComment.comment}</p>
                  </div>
                </div>
              )}

              {selectedApprovalComment.status === 'APPROVED' && !selectedApprovalComment.comment && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-gray-600 italic">{t('projectDetails.dialog.noCommentProvided')}</p>
                </div>
              )}
            </div>

            {/* Modal Actions */}
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setSelectedApprovalComment(null)}
                className="px-3 py-1.5 text-sm bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors font-medium shadow-sm"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approval Details Modal */}
      {showApprovalDetails && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-4">
          <div className="relative top-4 sm:top-10 mx-auto p-4 sm:p-6 border w-full max-w-5xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4 sm:mb-6">
              <h3 className="text-2xl font-semibold text-gray-900">
                {t('projectDetails.approvals.title')}
              </h3>
              <button
                onClick={() => setShowApprovalDetails(false)}
                className="text-gray-400 hover:text-gray-600 text-3xl font-light leading-none"
              >
                ×
              </button>
            </div>

            {/* Approval Requests List */}
            <div className="overflow-y-auto max-h-[70vh]">
              {approvalRequests && approvalRequests.length > 0 ? (
                <div className="space-y-6">
                  {approvalRequests.map((request) => (
                    <div
                      key={request.id}
                      className="border border-gray-200 rounded-lg p-5 hover:bg-gray-50 transition-colors"
                    >
                      {/* Request Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="text-lg font-semibold text-gray-800">
                              {request.approverName}
                            </h4>
                            {getApprovalStatusBadge(request.status)}
                          </div>
                          <p className="text-sm text-gray-600">
                            <strong>{t('projectDetails.approvals.email')}:</strong> {request.approverEmail}
                          </p>
                          <div className="flex gap-4 mt-2 text-xs text-gray-500">
                            <span>
                              <strong>{t('projectDetails.approvals.sent')}:</strong> {new Date(request.createdAt).toLocaleString()}
                            </span>
                            {request.status !== 'EXPIRED' && (
                              <span>
                                <strong>{t('projectDetails.approvals.expires')}:</strong> {new Date(request.expiresAt).toLocaleString()}
                              </span>
                            )}
                            {request.updatedAt !== request.createdAt && (
                              <span>
                                <strong>{t('projectDetails.approvals.updated')}:</strong> {new Date(request.updatedAt).toLocaleString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Translation Approvals */}
                      {request.translationApprovals && request.translationApprovals.length > 0 && (
                        <div className="mt-4">
                          <h5 className="text-sm font-semibold text-gray-700 mb-2">
                            {t('projectDetails.info.translations')} ({request.translationApprovals.length})
                          </h5>
                          <div className="space-y-2">
                            {request.translationApprovals.map((approval) => (
                              <div
                                key={approval.id}
                                className="bg-white border border-gray-200 rounded p-3"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <p className="font-medium text-gray-800">
                                      {approval.translation?.originalFileName || t('common.unknownDocument')}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {approval.translation?.sourceLanguage?.toUpperCase()} → {approval.translation?.targetLanguage?.toUpperCase()}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {getApprovalStatusBadge(approval.status)}
                                  </div>
                                </div>
                                {approval.reviewComment && (
                                  <div className="mt-2 p-2 bg-blue-50 rounded text-sm text-gray-700">
                                    <strong>{t('projectDetails.status.comment')}:</strong> {approval.reviewComment}
                                  </div>
                                )}
                                {approval.approvedAt && (
                                  <p className="mt-1 text-xs text-gray-500">
                                    {t('projectDetails.status.reviewed')}: {new Date(approval.approvedAt).toLocaleString()}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <div className="text-5xl mb-4">📧</div>
                  <p className="text-lg">{t('projectDetails.dialog.noApprovalRequests')}</p>
                  <p className="text-sm mt-2">{t('projectDetails.dialog.clickRequestApproval')}</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="mt-6 flex justify-between items-center">
              <button
                onClick={handleRequestApproval}
                className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors font-medium shadow-sm"
              >
                ✉️ {t('projectDetails.requestApproval')}
              </button>
              <button
                onClick={() => setShowApprovalDetails(false)}
                className="px-3 py-1.5 text-sm bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors font-medium shadow-sm"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sign Warning Dialog */}
      {signWarningDialog.isOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative mx-auto p-4 sm:p-6 border w-full max-w-lg shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4 sm:mb-6">
              <h3 className="text-base sm:text-xl font-semibold text-gray-900">
                ⚠️ {t('projectDetails.modals.pendingItems')}
              </h3>
              <button
                onClick={cancelSignWarning}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <p className="text-gray-700">
                {t('projectDetails.dialog.pendingItemsMessage')}
              </p>

              {signWarningDialog.pendingApprovals > 0 && (
                <div className="flex items-center gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <span className="text-2xl">⏳</span>
                  <div>
                    <p className="font-semibold text-yellow-900">
                      {signWarningDialog.pendingApprovals} {signWarningDialog.pendingApprovals > 1 ? t('projectDetails.dialog.pendingApprovalsPlural') : t('projectDetails.dialog.pendingApprovals')}
                    </p>
                    <p className="text-sm text-yellow-800">
                      {t('projectDetails.dialog.waitingForReview')}
                    </p>
                  </div>
                </div>
              )}

              {signWarningDialog.openComments > 0 && (
                <div className="flex items-center gap-3 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <span className="text-2xl">💬</span>
                  <div>
                    <p className="font-semibold text-orange-900">
                      {signWarningDialog.openComments} {signWarningDialog.openComments > 1 ? t('projectDetails.dialog.openCommentsPlural') : t('projectDetails.dialog.openComment')}
                    </p>
                    <p className="text-sm text-orange-800">
                      {t('projectDetails.dialog.unresolvedSuggestions')}
                    </p>
                  </div>
                </div>
              )}

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  {t('projectDetails.dialog.recommendResolve')}
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={cancelSignWarning}
                className="px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={proceedToSignatureView}
                className="px-3 py-1.5 text-sm bg-green-300 rounded-md hover:bg-green-400 transition-colors font-medium shadow-sm"
              >
                {t('projectDetails.actions.proceedToSign')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Signature & Stamp View */}
      {showSignatureView && (
        <SignatureStampView
          translations={selectedSigningTranslation ? [selectedSigningTranslation] : translations.filter(t => t.status === 'COMPLETED')}
          projectName={selectedSigningTranslation?.originalFileName || project.name}
          projectId={project.id}
          documentHolderName={project.clientName || project.name}
          userProfile={userProfile}
          sourceLanguage={selectedSigningTranslation?.sourceLanguage || project.sourceLanguage}
          targetLanguage={selectedSigningTranslation?.targetLanguage || project.targetLanguage}
          finalDocuments={selectedSigningTranslation ? (selectedSigningTranslation.finalDocuments || []) : (project.finalDocuments || [])}
          conformityNumberSeed={selectedSigningTranslation?.documentId || project.id}
          onClose={() => {
            setShowSignatureView(false);
            setSelectedSigningTranslation(null);
          }}
          onSave={handleSaveSignedDocument}
          onSendToClient={selectedSigningTranslation ? null : (selectedVersion) => openSendToClientModal(selectedVersion?.version || null)}
        />
      )}

      {/* Final Document Ready Notice */}
      {recentFinalDocument && !finalDocument && (
        <div className="fixed bottom-4 right-4 z-40 w-[min(92vw,420px)] bg-white border border-green-200 shadow-xl rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-full bg-green-100 text-green-700 shrink-0">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900">
                {t('projectDetails.finalDocument.readyTitle', 'Final version created')}
              </p>
              <p className="mt-0.5 text-xs text-gray-600">
                {t('projectDetails.finalDocument.readyDescription', 'The signed final document was saved. Open the preview only when you need to view it.')}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleViewFinalDocument(recentFinalDocument)}
                  disabled={loadingFinalPreview}
                  className="inline-flex items-center gap-1.5 rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loadingFinalPreview && (
                    <span className="h-3.5 w-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                  )}
                  {loadingFinalPreview
                    ? t('projectDetails.finalDocument.loadingPreview', 'Loading...')
                    : t('projectDetails.finalDocument.viewPdf', 'View final PDF')}
                </button>
                {recentFinalDocument.downloadUrl && (
                  <a
                    href={recentFinalDocument.downloadUrl}
                    className="inline-flex items-center rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    {t('projectDetails.finalDocument.downloadPdf', 'Download')}
                  </a>
                )}
                <button
                  type="button"
                  onClick={() => setRecentFinalDocument(null)}
                  className="inline-flex items-center rounded-md px-2 py-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                >
                  {t('common.dismiss', 'Dismiss')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Final Document Viewer */}
      {finalDocument && (
        <FinalDocumentViewer
          finalDocument={finalDocument}
          onClose={handleCloseFinalDocument}
          onSendToClient={() => openSendToClientModal(finalDocument?.version || null)}
        />
      )}

      {/* Send Final Document to Client Modal */}
      <ApprovalRequestModal
        isOpen={showSendToClientModal}
        title={sendToClientTargetVersion
          ? t('projectDetails.modals.sendFinalDocumentVersion', { version: sendToClientTargetVersion, defaultValue: `Send Final Document (Version ${sendToClientTargetVersion}) to Client` })
          : t('projectDetails.modals.sendFinalDocument', 'Send Final Document to Client')}
        description={sendToClientTargetVersion
          ? t('projectDetails.modals.sendFinalDocumentVersionDesc', { version: sendToClientTargetVersion, defaultValue: `Version ${sendToClientTargetVersion} will be sent to the client by email.` })
          : t('projectDetails.modals.sendFinalDocumentDesc', 'The signed final document will be sent to the client by email.')}
        approverName={sendToClientForm.approverName}
        approverEmail={sendToClientForm.approverEmail}
        approverLanguage={sendToClientForm.approverLanguage}
        includeDocuments={sendToClientForm.includeDocuments}
        signedDocumentOptions={signedDocumentOptions}
        selectedSignedDocumentIds={selectedSignedDocumentIds}
        allowDocumentUpload={true}
        uploadedFiles={sendToClientFiles}
        uploadError={sendToClientUploadError}
        onFilesChange={handleSendToClientFilesChange}
        onFieldChange={handleSendToClientFieldChange}
        onCancel={() => {
          setShowSendToClientModal(false);
          setSendToClientTargetVersion(null);
          setSelectedSignedDocumentIds([]);
          setSendToClientFiles([]);
          setSendToClientUploadError('');
          setSendToClientForm({ approverName: '', approverEmail: '', approverLanguage: 'fr', includeDocuments: false, includeCombinedFinal: false });
        }}
        onSubmit={handleSendToClient}
        isSubmitting={isSendingToClient}
        submitLabel={t('projectDetails.actions.sendToClient', 'Send to Client')}
        accent="blue"
      />

      {/* Comments Modal */}
      {selectedDocumentComments && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-4">
          <div className="relative top-4 sm:top-10 mx-auto p-4 sm:p-6 border w-full max-w-4xl shadow-lg rounded-md bg-white max-h-[85vh] flex flex-col">
            <div className="flex justify-between items-center mb-4 sm:mb-6">
              <div className="flex-1 min-w-0 pr-2">
                <h3 className="text-base sm:text-2xl font-semibold text-gray-900 truncate">
                  {t('projectDetails.modals.commentsOn')} {selectedDocumentComments.translation.originalFileName}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedDocumentComments.comments.length} {t('projectDetails.translations.openComments')}
                </p>
              </div>
              <button
                onClick={() => setSelectedDocumentComments(null)}
                className="text-gray-400 hover:text-gray-600 text-3xl font-light leading-none"
              >
                ×
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {selectedDocumentComments.comments.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <div className="text-5xl mb-4">✅</div>
                  <p className="text-lg">{t('projectDetails.dialog.noOpenComments')}</p>
                  <p className="text-sm mt-2">{t('projectDetails.dialog.allFeedbackAddressed')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {selectedDocumentComments.comments.map((comment, index) => (
                    <div
                      key={comment.id || index}
                      className="border border-gray-200 rounded-lg p-4 bg-white hover:bg-gray-50"
                    >
                      {/* Comment Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
                            {comment.author?.displayName?.charAt(0) || '?'}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-800">
                              {comment.author?.displayName || t('common.unknownUser')}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(comment.createdTime).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        {comment.resolved && (
                          <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                            {t('projectDetails.status.resolved')}
                          </span>
                        )}
                      </div>

                      {/* Comment Content */}
                      <div className="ml-13 mb-3">
                        <p className="text-gray-700 whitespace-pre-wrap">
                          {comment.content}
                        </p>
                      </div>

                      {/* Quoted Text (if available) */}
                      {comment.quotedFileContent?.value && (
                        <div className="ml-13 mb-3 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                          <p className="text-xs text-gray-600 font-medium mb-1">{t('projectDetails.status.commentedOn')}:</p>
                          <p className="text-sm text-gray-700 italic">
                            "{comment.quotedFileContent.value}"
                          </p>
                        </div>
                      )}

                      {/* Replies */}
                      {comment.replies && comment.replies.length > 0 && (
                        <div className="ml-13 mt-3 space-y-2">
                          {comment.replies.map((reply, replyIndex) => (
                            <div
                              key={reply.id || replyIndex}
                              className="flex items-start gap-2 p-3 bg-gray-50 rounded"
                            >
                              <div className="w-8 h-8 bg-gray-400 rounded-full flex items-center justify-center text-white text-sm">
                                {reply.author?.displayName?.charAt(0) || '?'}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <p className="font-medium text-sm text-gray-800">
                                    {reply.author?.displayName || t('common.unknownUser')}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {new Date(reply.createdTime).toLocaleString()}
                                  </p>
                                </div>
                                <p className="text-sm text-gray-700">
                                  {reply.content}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Actions */}
                      <div className="ml-13 mt-3 flex gap-2">
                        <a
                          href={`https://docs.google.com/document/d/${selectedDocumentComments.translation.googleDocId}/edit`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {t('projectDetails.dialog.viewInGoogleDocs')} →
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-between items-center">
              <a
                href={`https://docs.google.com/document/d/${selectedDocumentComments.translation.googleDocId}/edit`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium shadow-sm"
              >
                {t('projectDetails.dialog.openInGoogleDocs')}
              </a>
              <button
                onClick={() => setSelectedDocumentComments(null)}
                className="px-3 py-1.5 text-sm bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors font-medium shadow-sm"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default ProjectDetails;
